
import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Box,
  TableSortLabel,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BACKEND_BASE_URL } from "../../config";

const extractApiErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallbackMessage;
};

const CityAgreement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "agreement_start_date", direction: "desc" }); // default newest first
  const token = localStorage.getItem("authToken");

  // edit dialog state
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [townConfigs, setTownConfigs] = useState({});
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCityAdminIds, setSelectedCityAdminIds] = useState([]);

  useEffect(() => {
    fetchCities();
    fetchTownConfigs();
    fetchPartnerUsers();
  }, []);

  // Requirement: no scrollbar on this page.
  // We want no vertical scrollbar, but we DO want horizontal scrolling for the wide table.
  useEffect(() => {
    const prevOverflowY = document.body.style.overflowY;
    const prevOverflowX = document.body.style.overflowX;
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = prevOverflowY;
      document.body.style.overflowX = prevOverflowX;
    };
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_BASE_URL}/api/partner-cities/`);
      setRows(res.data);
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTownConfigs = async () => {
    try {
      const res = await axios.get(`${BACKEND_BASE_URL}/api/town-admin/town-configs/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const byTownId = {};
      (res.data || []).forEach((cfg) => {
        byTownId[cfg.partner_city_id] = cfg;
      });
      setTownConfigs(byTownId);
      return byTownId;
    } catch (err) {
      console.error("Error fetching town configs:", err);
      return {};
    }
  };

  const fetchPartnerUsers = () => {
    axios
      .get(`${BACKEND_BASE_URL}/api/admin-users/?page_size=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const users = (res.data?.results || [])
          .filter((u) => !!u.is_partner && !!u.is_active)
          .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
        setPartnerUsers(users);
      })
      .catch((err) => console.error("Error fetching partner users:", err));
  };

  // 🔹 Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // 🔹 Apply search + sort
  const filteredAndSortedRows = rows
    .filter((row) =>
      `${row.city_name} ${row.country_name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;
      if (sortConfig.direction === "asc") {
        return new Date(a[sortConfig.key]) - new Date(b[sortConfig.key]);
      } else {
        return new Date(b[sortConfig.key]) - new Date(a[sortConfig.key]);
      }
    });

  const handleEdit = async (row) => {
    setSelectedRow(row);
    setFormData(row);
    // For the City Admin User selector inside the Edit dialog.
    const existing = townConfigs[row.id];
    if (existing?.assigned_city_admin_ids) {
      setSelectedCityAdminIds(existing.assigned_city_admin_ids.map(Number));
    } else {
      // If town configs aren't loaded yet, fetch before opening so checkboxes render correctly.
      const byTownId = await fetchTownConfigs();
      const cfg = byTownId[row.id];
      setSelectedCityAdminIds((cfg?.assigned_city_admin_ids || []).map(Number));
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    setFormData({});
    setSelectedCityAdminIds([]);
  };

  const openAssignDialog = (row) => {
    const config = townConfigs[row.id];
    setSelectedRow(row);
    setSelectedCityAdminIds((config?.assigned_city_admin_ids || []).map(Number));
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedCityAdminIds([]);
    setSelectedRow(null);
  };

  const handleSaveAssignedCityAdmin = () => {
    if (!selectedRow) return;
    axios
      .patch(
        `${BACKEND_BASE_URL}/api/town-admin/town-configs/${selectedRow.id}/`,
        {
          assigned_city_admin_ids: selectedCityAdminIds || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        toast.success("City admin assignment updated.");
        fetchTownConfigs();
        closeAssignDialog();
      })
      .catch((err) => {
        console.error("Error assigning city admin:", err);
        toast.error(extractApiErrorMessage(err, "Failed to update city admin assignment."));
      });
  };

  const handleResetTown = (row) => {
    if (
      !window.confirm(
        `Reset town portal settings to defaults for ${row.city_name}? This clears town admin config, publication overrides, and city admin assignments.`
      )
    ) {
      return;
    }
    axios
      .post(
        `${BACKEND_BASE_URL}/api/town-admin/towns/${row.id}/reset/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        toast.success("Town settings reset to defaults.");
        fetchTownConfigs();
      })
      .catch((err) => {
        console.error("Error resetting town:", err);
        const detail = err?.response?.data?.detail || err?.message || "Reset failed.";
        toast.error(typeof detail === "string" ? detail : "Reset failed.");
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedRow) return;
    setEditSaving(true);
    const partnerCityId = selectedRow.id;
    const nextAssignedCityAdminIds = selectedCityAdminIds || [];

    const data = new FormData();
    data.append("agreement_start_date", formData.agreement_start_date || "");
    data.append("agreement_end_date", formData.agreement_end_date || "");
    data.append("reminder_days", formData.reminder_days || 0);
    data.append("notes", formData.notes || "");

    // 🔹 New fields
    data.append("explain_requests_per_user", formData.explain_requests_per_user || 0);
    data.append("ask_aware_requests_per_user", formData.ask_aware_requests_per_user || 0);
    if (formData.attachment instanceof File) {
      data.append("attachment", formData.attachment);
    }

    try {
      // 1) Update agreement fields (+ attachment) using multipart/form-data.
      await axios.patch(
        `${BACKEND_BASE_URL}/api/partner-cities/${partnerCityId}/edit/`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Let Axios set the correct multipart boundary automatically.
            // Setting Content-Type manually can break parsing on the backend.
          },
        }
      );

      // 2) Update multi city admins using JSON patch (multipart parsing would lose list shape).
      await axios.patch(
        `${BACKEND_BASE_URL}/api/town-admin/town-configs/${partnerCityId}/`,
        {
          assigned_city_admin_ids: nextAssignedCityAdminIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh UI state.
      await Promise.all([fetchCities(), fetchTownConfigs()]);
      toast.success("City agreement updated.");
      handleClose();
    } catch (err) {
      console.error("Error updating city:", err);
      toast.error(extractApiErrorMessage(err, "Failed to update city agreement."));
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleLock = (row) => {
    axios
      .patch(
        `${BACKEND_BASE_URL}/api/partner-cities/${row.id}/edit/`,
        { unlock_city: !row.unlock_city },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        fetchCities();
        toast.success(row.unlock_city ? "City locked successfully." : "City unlocked successfully.");
      })
      .catch((err) => {
        console.error("Error toggling lock:", err);
        toast.error(extractApiErrorMessage(err, "Failed to update city lock status."));
      });
  };

  // 🔹 Expiration check
  const isReminderReached = (row) => {
    if (!row.agreement_end_date || !row.reminder_days) return false;
    const endDate = new Date(row.agreement_end_date);
    const today = new Date();
    const reminderDate = new Date(endDate);
    reminderDate.setDate(endDate.getDate() - row.reminder_days);
    return today >= reminderDate;
  };

  // 🔹 Check if agreement is expired
  const isExpired = (row) => {
    if (!row.agreement_end_date) return false;
    const endDate = new Date(row.agreement_end_date);
    const today = new Date();
    return today > endDate;
  };

  return (
    <Box sx={{ p: 2 }}>
      <ToastContainer position="top-right" autoClose={4000} />
      {/* 🔹 Header with Search + Export */}
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography style={{ fontWeight: "bold" }} variant="h4">
          City Agreements
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by city/country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.open(`${BACKEND_BASE_URL}/api/partner-cities/export/csv/`, "_blank")}
          >
            Export CSV
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto" }}>

        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
          </div>
        ) : (
          <Table
            size="small"
            sx={{
              width: "100%",
              minWidth: 1200,
              tableLayout: "auto",
              "& .MuiTableCell-root": {
                paddingTop: "6px",
                paddingBottom: "6px",
                fontSize: "12px",
              },
              "& .MuiButton-root": {
                minWidth: "auto",
              },
            }}
          >
            <TableHead sx={{ backgroundColor: "#152F8F" }}>
              <TableRow>
                <TableCell sx={{ color: 'white' }}><strong>Country</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>City</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>State</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>County</strong></TableCell>
                <TableCell >
                  <TableSortLabel
                    active={sortConfig.key === "agreement_start_date"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("agreement_start_date")}
                    sx={{
                      color: "white !important", // text color
                      "&:hover": {
                        color: "yellow !important", // hover color
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important", // sort arrow white
                      },
                      "&:hover .MuiTableSortLabel-icon": {
                        color: "yellow !important", // sort arrow yellow on hover
                      },
                    }}
                  >
                    <strong>
                      Start Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell >
                  <TableSortLabel
                    active={sortConfig.key === "agreement_end_date"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("agreement_end_date")}
                    sx={{
                      color: "white !important", // text color
                      "&:hover": {
                        color: "yellow !important", // hover color
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important", // sort arrow white
                      },
                      "&:hover .MuiTableSortLabel-icon": {
                        color: "yellow !important", // sort arrow yellow on hover
                      },
                    }}

                  >
                    <strong>
                      End Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white' }}><strong>Aware Explain</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>Ask Aware</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>Agreement Status</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>Attachment</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>Actions</strong></TableCell>
                <TableCell sx={{ color: 'white' }}><strong>Lock/Unlock</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedRows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: isReminderReached(row) ? "#ffebee" : "inherit", // 🔹 highlight expired/reminder
                  }}
                >
                  <TableCell>{row.country_name}</TableCell>
                  <TableCell>{row.city_name}</TableCell>
                  <TableCell>{row.city_state}</TableCell>
                  <TableCell>{row.city_county}</TableCell>
                  <TableCell>{row.agreement_start_date}</TableCell>
                  <TableCell>
                    {row.agreement_end_date}
                    {isExpired(row) && (
                      <Chip label="Expired" color="error" size="small" sx={{ ml: 1 }} />
                    )}
                    {!isExpired(row) && isReminderReached(row) && (
                      <Chip label="Expiring Soon" color="warning" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{row.explain_requests_per_user || 0}</TableCell>
                  <TableCell>{row.ask_aware_requests_per_user || 0}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    {row.attachment ? (
                      <a
                        href={`${BACKEND_BASE_URL}${row.attachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1976d2" }}
                      >
                        View File
                      </a>
                    ) : (
                      "No File"
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      <Tooltip
                        title={row.unlock_city ? "Edit city agreement" : "This city is locked. You cannot edit it."}
                        arrow
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleEdit(row)}
                            disabled={!row.unlock_city}
                            sx={{ minWidth: "auto", padding: "4px 12px", fontSize: 12, lineHeight: 1.2 }}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reset town portal settings to defaults (Content Queuing, start date, publications, admins)" arrow>
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleResetTown(row)}
                            aria-label="Reset town settings to defaults"
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color={row.unlock_city ? "error" : "success"}
                      size="small"
                      onClick={() => handleToggleLock(row)}
                      sx={{ minWidth: "auto", padding: "4px 12px", fontSize: 12, lineHeight: 1.2 }}
                    >
                      {row.unlock_city ? "Lock City" : "Unlock City"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* 🔹 Edit Dialog */}
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
          <DialogTitle>Edit City Agreement</DialogTitle>
          <DialogContent sx={{ maxHeight: "70vh", position: "relative" }}>
            <TextField
              margin="dense"
              name="agreement_start_date"
              label="Start Date"
              type="date"
              value={formData.agreement_start_date || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              margin="dense"
              name="agreement_end_date"
              label="End Date"
              type="date"
              value={formData.agreement_end_date || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              margin="dense"
              name="explain_requests_per_user"
              label="Explain Requests Per User"
              type="number"
              value={formData.explain_requests_per_user || ""}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              margin="dense"
              name="ask_aware_requests_per_user"
              label="Ask Aware Requests Per User"
              type="number"
              value={formData.ask_aware_requests_per_user || ""}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              margin="dense"
              name="reminder_days"
              label="Reminder Days"
              type="number"
              value={formData.reminder_days || ""}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              margin="dense"
              name="notes"
              label="Notes"
              multiline
              rows={3}
              value={formData.notes || ""}
              onChange={handleChange}
              fullWidth
            />
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, attachment: e.target.files[0] }))
              }
              style={{ marginTop: "16px" }}
            />
            {formData.attachment && typeof formData.attachment === "string" && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Current File:{" "}
                <a
                  href={`${BACKEND_BASE_URL}${formData.attachment}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="edit-city-admin-select-label">Select City Admin User</InputLabel>
                <Select
                  labelId="edit-city-admin-select-label"
                  multiple
                  value={selectedCityAdminIds}
                  label="Select City Admin User"
                  disabled={editSaving}
                  MenuProps={{
                    // Important: MUI menus render in a portal by default, which won't be covered/blurred
                    // by our dialog overlay. Keeping it in the dialog ensures it is blurred too.
                    disablePortal: true,
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    const arr = Array.isArray(value) ? value : [value];
                    setSelectedCityAdminIds(arr.map((v) => Number(v)));
                  }}
                >
                  {partnerUsers.map((user) => (
                    <MenuItem key={user.id} value={Number(user.id)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox checked={selectedCityAdminIds.includes(Number(user.id))} />
                        <span>
                          {user.name || user.email} ({user.email})
                        </span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {editSaving && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255,255,255,0.45)",
                  backdropFilter: "blur(3px)",
                  WebkitBackdropFilter: "blur(3px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={editSaving}>
              {editSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={assignDialogOpen} onClose={closeAssignDialog} fullWidth maxWidth="sm" scroll="paper">
          <DialogTitle>Select City Admin User</DialogTitle>
          <DialogContent sx={{ maxHeight: "70vh" }}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="city-admin-select-label">City Admin User</InputLabel>
              <Select
                labelId="city-admin-select-label"
                multiple
                value={selectedCityAdminIds}
                label="City Admin User"
                MenuProps={{
                  disablePortal: true,
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const arr = Array.isArray(value) ? value : [value];
                  setSelectedCityAdminIds(arr.map((v) => Number(v)));
                }}
              >
                {partnerUsers.map((user) => (
                  <MenuItem key={user.id} value={Number(user.id)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox checked={selectedCityAdminIds.includes(Number(user.id))} />
                      <span>
                        {user.name || user.email} ({user.email})
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAssignDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveAssignedCityAdmin}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </TableContainer>
    </Box>
  );
};

export default CityAgreement;